/* eslint-disable indent */
import axios, { CancelToken } from 'axios';
import { Attribute } from 'base/types/Attributes';
import {
  RecommendationInputBody,
  RecommendationInputResponse,
  TPinInputApiResponse,
} from 'base/types/RecommendationInput';
import { FieldSoilRequest } from './../types/SoilType';
import { SoilTypeResponse } from 'base/types/SoilType';
import API from './config';
import {
  RecCropwiseApiInputs,
  RecommendationCropwiseResponse,
} from './../types/RecommendationCropwise';
import { BreakoutsGlobalResponse } from 'base/types/BreakoutsGlobalResponse';
import { ProductSeed } from 'base/types/Products';
import { WeatherResponse } from 'base/types/Weather';

import { objectToQueryParams } from 'utils/urlSearchParams';
import { AttributesForFields, TPPForFields } from 'base/types/Fields';
import { decrypt, encrypt } from '../../utils/crypto';
import { RecaptchaPayload, RecaptchaAPIResponse } from 'base/types/Recaptcha';
import { BreakoutsProductsSummaryResponse } from 'base/types/BreakoutsProductsSummaryResponse';
import { DetectFieldResponse, DetectFieldPayload } from 'base/types/detectField';
import { UserDetails } from 'base/types/UserDetails';
import { GrowthStagePayload } from 'base/types/growthStage';
import { AbioticStagePayload } from 'base/types/AbioticStress';
import { CropConstants } from 'utils/constants/Crop';
import track from 'utils/amplitudeWrapper';
import { PreBookSeedsPayload, PreBookSeedsResponse } from 'base/types/PreBookSeeds';

interface GetBreakoutsGlobaParams {
  productName: string;
  crop: string;
  country: string;
  yieldLevel?: string;
  texture?: string;
}

interface GetCountriesParams {
  params?: { filterBy?: string };
  cancelToken?: CancelToken;
}

interface GetAttributesParams {
  params: { crop?: string; countryCode?: string; cropId?: number | string; usageGroups?: string[] };
  cancelToken?: CancelToken;
}

interface GetFieldSoilsParams {
  params: { fields?: FieldSoilRequest[]; cropRegion?: string; brandName?: string };
  cancelToken?: CancelToken;
}

interface GetProductMaturityRangeFAOParams {
  params: { crop?: string; countryCode?: string };
  cancelToken?: CancelToken;
}

interface GetProductsParams {
  params: { crop?: string; countryCode?: string };
  cancelToken?: CancelToken;
}

interface GetWeatherParams {
  lat: number;
  lng: number;
  startDate: string;
  endDate: string;
  cancelToken?: CancelToken;
}

interface ISendMarketingCommercialEmail {
  recommendationSolutionId?: string;
  recommendationCropwise?: any; // Full recommendation object for marketing-email-scripts endpoint
  utm_source?: string;
  utm_content?: string;
  utm_term?: string;
  utm_campaign?: string;
  utm_medium?: string;
  sipIntegrationId?: number;
}

type MarketingEmailEndpoint = 'marketing-email' | 'marketing-email-scripts';

interface GetTPPAttributes {
  params: { countryCode?: string; cropId?: number | string; centroid: number[] | [] };
  cancelToken?: CancelToken;
}

interface GetFieldsAttributes {
  params: { country?: string; cropId?: number | string; long: number; lat: number };
  cancelToken?: CancelToken;
}
interface GetDownyMildewAttributes {
  params: { tppRegion: string; cropRotation: string; tillageSystem: string; soilType: string };
  cancelToken?: CancelToken;
}

interface VerifyLandArableParams {
  params: {
    latitude: number;
    longitude: number;
  };
  cancelToken?: CancelToken;
}

export interface VaReportPayload {
  report_id: string;
  email: string;
}

export const buildAttributesEndpoint = ({
  crop,
  countryCode,
  cropId,
  usageGroups,
}: {
  crop?: string;
  countryCode?: string;
  cropId?: number | string;
  usageGroups?: string[];
}) => {
  let endpoint = '/attributes';

  if (crop || countryCode || cropId || (usageGroups && usageGroups.length > 0)) {
    endpoint += '?';

    if (crop) {
      endpoint += `crop=${crop}`;
    }

    if (countryCode) {
      if (crop) {
        endpoint += '&';
      }
      endpoint += `countryCode=${countryCode}`;
    }

    if (cropId) {
      if (crop || countryCode) {
        endpoint += '&';
      }
      endpoint += `cropId=${cropId}`;
    }

    if (usageGroups && usageGroups.length > 0) {
      if (crop || countryCode || cropId) {
        endpoint += '&';
      }
      endpoint += `usageGroups=${usageGroups.join(',')}`;
    }
  }
  return endpoint;
};

class ProxyLayer {
  private _api;
  private _api_uat;

  constructor() {
    this._api = API(`${process.env.REACT_APP_PROXY_LAYER_URL}/proxy-layer/v1`);
    this._api_uat = API(`${process.env.REACT_APP_PROXY_LAYER_URL}/proxy-layer/v1`);
  }

  cancelToken() {
    return axios.CancelToken.source();
  }

  async getCountries(config?: GetCountriesParams) {
    const { params, cancelToken } = config ?? {};
    const res = await this._api.get('/countries', {
      cancelToken,
      params,
    });
    return res.data;
  }

  capitalizeWords(words: string) {
    return words
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  async getAttributes(config: GetAttributesParams) {
    const endpoint = buildAttributesEndpoint(config.params);
    const response = await this._api.get(endpoint);
    // sort attributes
    const sortedAttributes = response.data
      .sort(
        (attribute_a: Attribute, attribute_b: Attribute) =>
          attribute_a.orderNumber - attribute_b.orderNumber
      )
      .map((attribute: Attribute) => {
        return {
          ...attribute,
          attributeOriginal: attribute.attributeName,
          attributeName: this.capitalizeWords(attribute.attributeName),
        };
      });
    return sortedAttributes as Attribute[];
  }

  async getFieldSoils(config: GetFieldSoilsParams) {
    const { fields, cropRegion, brandName } = config.params;
    const response = await this._api.post('/soils', {
      fields,
      cropRegion,
      brandName,
    });
    return response.data as SoilTypeResponse[];
  }

  async getProducts(config: GetProductsParams): Promise<ProductSeed[]> {
    const { crop, countryCode } = config.params;
    let cropName = crop;
    if (crop === CropConstants.WINTER_OILSEED_RAPE) {
      cropName = CropConstants.WOSR;
    } else if (crop === CropConstants.HYBRID_BARLEY) {
      cropName = CropConstants.BARLEY;
    } else if (countryCode === CropConstants.Brazil) {
      if (crop?.toLowerCase() === CropConstants.CORN_SMALLCASE) {
        cropName = CropConstants.GRAIN_CORN;
      }
      if (crop?.toLowerCase().includes(CropConstants.SOYBEAN_SMALLCASE)) {
        cropName = CropConstants.SOYBEANS;
      }
    }
    const response = await this._api.get('/products', {
      params: { country: String(countryCode).trim(), crop: cropName },
      cancelToken: config.cancelToken,
    });

    return response.data;
  }

  async getProductMaturityRangeFAO(config: GetProductMaturityRangeFAOParams) {
    const { crop, countryCode } = config.params;
    const response = await this._api.get(`/products/fao?crop=${crop}&country=${countryCode}`, {
      params: { country: String(countryCode).trim(), crop },
    });
    return response.data;
  }

  async postRecommendationInput(body: RecommendationInputBody) {
    const response = await this._api.post('/recommendation-input', body);
    return response.data;
  }

  async isSyngentaUser(email: string) {
    try {
      const result = await this._api_uat.get(`/is-syngenta-user?email=${email}`);
      return result.data;
    } catch (e) {
      return { status: false };
    }
  }

  formatIds(data: any) {
    try {
      data.recommendations.forEach((r: any) => {
        if (
          r.multi_field.assignments.length === 1 &&
          r.multi_field.assignments[0].field_id !==
            r.multi_field.geometry.properties[0].fields[0].id
        ) {
          r.multi_field.geometry.properties[0].fields[0].id = r.multi_field.assignments[0].field_id;
        }
      });
    } catch (e) {
      console.log(e);
    }
    return data;
  }

  async getRecommendationInput(recommendation_id: string, cancelToken?: CancelToken) {
    const response = await this._api.get(`/recommendation-input/${recommendation_id}`, {
      cancelToken,
    });
    return {
      ...response.data,
      productMaturityRange: JSON.parse(response.data.productMaturityRange),
      agronomicWeightings: JSON.parse(response.data.agronomicWeightings),
      boundaries: [].concat(response.data.boundaries).map((boundary: any) => ({
        ...boundary,
        selectedSoils: JSON.parse(boundary.selectedSoils || '[]'),
        soils: JSON.parse(boundary.soils || '[]'),
        fieldsResolution: boundary.fieldsResolution || 0,
      })),
      pins: [].concat(response.data.pins).map((pin: TPinInputApiResponse) => ({
        ...pin,
        selectedSoils: JSON.parse(pin.selectedSoils || '[]'),
        soils: JSON.parse(pin.soils || '[]'),
        fieldsResolution: pin.fieldsResolution || 0,
      })),
    } as RecommendationInputResponse;
  }

  async getRecommendationCropwise(input: RecCropwiseApiInputs, cancelToken?: CancelToken) {
    const recommendationPageURL =
      input?.source.toUpperCase() === 'DDG'
        ? `/recommendation-cropwise?solutionId=${input.solutionId}`
        : `/recommendation-cropwise?${input.decodedRecParamString}`;

    try {
      const response = await this._api.get(recommendationPageURL, {
        cancelToken,
      });

      if (response.data.recommendations[0].spot_check) {
        response.data.recommendations[0].multi_field = response.data.recommendations[0].spot_check;
      }
      if (response.data.source.toLowerCase() === 'ddg') {
        response.data.is_ddg = true;
      }
      return this.formatIds(response.data) as RecommendationCropwiseResponse;
    } catch (error: any) {
      if (error.response?.status === 403) {
        track('recommendation_cropwise_403_error', {
          source: input?.source,
          solutionId: input?.solutionId,
          url: recommendationPageURL,
          error_message: error.message,
          timestamp: new Date().toISOString(),
        });
      }
      throw error;
    }
  }

  async getRecommendationValidated(body: RecommendationInputBody) {
    const response = await this._api.post(`/validate-recommendation`, body);
    return response.data;
  }

  async getBreakoutsGlobal({
    productName = 'SY_Solandri',
    crop = 'corn',
    country = 'Ukraine',
    yieldLevel,
    texture,
  }: GetBreakoutsGlobaParams) {
    let body: any = {
      entry: {
        Entry: productName,
        Crop: crop,
        Country: country,
        Region: 'EAME',
        Type: 'HIGH_NAME',
        IsTraitedVersion: false,
      },
      exportImages: false,
    };
    if (texture && yieldLevel) {
      body = {
        ...body,
        Filters: [
          { Key: 'Yield Level', Values: yieldLevel },
          { Key: 'Texture', Values: texture },
        ],
      };
    }
    const response = await this._api.post('/breakouts-global', body);
    const encryptedResponse = await decrypt(response.data);
    return JSON.parse(encryptedResponse) as unknown as BreakoutsGlobalResponse;
  }

  async getBreakoutsGlobalRecommendationId({
    solutionId = '',
    withFilters = false,
    activeProduct = '',
    source = '',
    decodedRecParamString = '',
    solutionType = '',
    rankingCrop = '',
    soilTexture = '',
    tpr = '',
    crop_name = '',
    country = '',
    countryRegion = '',
    yieldRange = '',
    rankings = {},
    yieldRangeUnit = '',
    yieldRank = {},
    SelectedSeeds = '',
  }: {
    solutionId: string;
    withFilters: boolean;
    activeProduct?: string;
    source: string;
    decodedRecParamString?: string;
    solutionType?: string;
    rankingCrop?: string;
    soilTexture?: string;
    tpr?: string;
    crop_name?: string;
    country?: string;
    countryRegion?: string;
    yieldRange?: string;
    rankings?: any;
    yieldRangeUnit?: string;
    yieldRank?: any;
    SelectedSeeds?: string;
  }) {
    const body: any = {
      solutionId,
      withFilters,
      activeProduct,
      source,
      ...(decodedRecParamString && { decodedRecParamString: decodedRecParamString }),
    };

    const bodyV2: any = {
      solutionId,
      withFilters,
      activeProduct,
      source,
      decodedRecParamString,
      solutionType,
      rankingCrop,
      soilTexture,
      tpr,
      crop_name,
      country,
      countryRegion,
      yieldRange,
      rankings,
      yieldRangeUnit,
      yieldRank,
      SelectedSeeds,
    };

    const config = {
      headers: { Authorization: `Bearer ${process.env.REACT_APP_API_TOKEN_BREAKOUTS_GLOBAL}` },
    };
    // const response = await this._api.post('/breakouts-global', body, config);
    const response = await this._api.post('/breakouts-global-v2', bodyV2, config);
    const encryptedResponse = await decrypt(response.data);
    return JSON.parse(encryptedResponse) as unknown as BreakoutsGlobalResponse;
  }

  async getProductSummaryRecommendationId({
    solutionId,
    withFilters = false,
    activeProduct = '',
    source,
    decodedRecParamString,
    solutionType = '',
    rankingCrop = '',
    soilTexture = '',
    tpr = '',
    crop_name = '',
    country = '',
    countryRegion = '',
    yieldRange = '',
    rankings = {},
    yieldRangeUnit = '',
    yieldRank = {},
    SelectedSeeds = '',
  }: {
    solutionId: string;
    withFilters: boolean;
    activeProduct?: string;
    source: string;
    decodedRecParamString?: string;
    solutionType?: string;
    rankingCrop?: string;
    soilTexture?: string;
    tpr?: string;
    crop_name?: string;
    country?: string;
    countryRegion?: string;
    yieldRange?: string;
    rankings?: any;
    yieldRangeUnit?: string;
    yieldRank?: any;
    SelectedSeeds?: string;
  }) {
    const body: any = {
      solutionId,
      withFilters,
      activeProduct,
      source,
      decodedRecParamString,
    };
    const bodyV2: any = {
      solutionId,
      withFilters,
      activeProduct,
      source,
      decodedRecParamString,
      solutionType,
      rankingCrop,
      soilTexture,
      tpr,
      crop_name,
      country,
      countryRegion,
      yieldRange,
      rankings,
      yieldRangeUnit,
      yieldRank,
      SelectedSeeds,
    };

    const config = {
      headers: { Authorization: `Bearer ${process.env.REACT_APP_API_TOKEN_BREAKOUTS_GLOBAL}` },
    };

    const response = await this._api.post('/breakouts-products-summary', body, config);

    const encryptedResponse = await decrypt(response.data);
    if (localStorage.getItem('debug-breakout')) {
      console.log(encryptedResponse, '<<<<BreakoutsProductsSummary');
    }
    return JSON.parse(encryptedResponse) as unknown as BreakoutsProductsSummaryResponse;
  }

  async renderPDF(body: { [key: string]: any }) {
    const response = await this._api.post('/render-pdf', body, {
      responseType: 'blob',
      headers: {
        Accept: 'application/pdf',
      },
    });
    return response.data;
  }

  async getWeather(config: GetWeatherParams): Promise<WeatherResponse[]> {
    const { lat, lng, startDate, endDate, cancelToken } = config;
    const response = await this._api.get(
      `/weather?plantingDate=${startDate}&harvestingDate=${endDate}&latitude=${lat}&longitude=${lng}&frequency=weekly`,
      {
        cancelToken,
      }
    );
    return response.data;
  }

  async sendMarketingCommercialEmail(
    params: ISendMarketingCommercialEmail,
    endpoint?: MarketingEmailEndpoint
  ) {
    const path = endpoint ?? 'marketing-email';

    // For marketing-email-scripts endpoint, send data in request body
    if (endpoint === 'marketing-email-scripts') {
      return await this._api.post(`/${path}`, params);
    }

    // For regular marketing-email endpoint, send as query parameters (legacy behavior)
    const queryParams = objectToQueryParams(params);
    return await this._api.post(`/${path}?${queryParams}`);
  }

  async resendMarketingCloudEmail(recommendationSolutionId: string) {
    await this._api.post('/marketing-cloud/resend-events', { recommendationSolutionId });
  }

  async resendMarketingCloudSms(
    recommendationSolutionId: string,
    userLanguage: string,
    phoneNumber?: string
  ) {
    await this._api.post('/marketing-cloud/send-sms', {
      recommendationSolutionId,
      userLanguage,
      phoneNumber,
    });
  }

  async updateContact(
    oldEmail: string,
    newEmail: string,
    newPhoneNumber: string,
    smsOptin: boolean,
    recommendationSolutionId: string
  ) {
    await this._api.post('/update-contact', {
      oldEmail,
      email: newEmail,
      newPhoneNumber,
      recommendationUUID: recommendationSolutionId,
      smsOptin,
    });
  }

  async getTPPForFields(config: GetTPPAttributes): Promise<TPPForFields> {
    const { cropId, countryCode, centroid } = config.params;
    const response = await this._api.post(
      `/tpp-zone?country=${countryCode}&id=${cropId}&type=attributes`,
      [centroid],
      {
        cancelToken: config.cancelToken,
      }
    );
    return response.data;
  }

  async getFieldsAttributes(config: GetFieldsAttributes): Promise<AttributesForFields> {
    const { country, cropId, long, lat } = config.params;
    const response = await this._api.get(
      `/check-attributes-for-fields?country=${country}&crop_id=${cropId}&longitude=${long}&latitude=${lat}`,
      {
        cancelToken: config.cancelToken,
      }
    );
    return response.data;
  }

  async getDownyMildew(config: GetDownyMildewAttributes): Promise<TPPForFields> {
    const { tppRegion, cropRotation, tillageSystem, soilType } = config.params;
    const response = await this._api.get(
      `/downy-mildew?tppRegion=${tppRegion}&cropRotation=${cropRotation}&tillageSystem=${tillageSystem}&soilType=${soilType}`,
      {
        cancelToken: config.cancelToken,
      }
    );
    return response.data;
  }

  async verifyRecaptcha(config: RecaptchaPayload): Promise<RecaptchaAPIResponse> {
    const { token } = config.params;

    // Encrypt the token before sending to backend
    const encryptedToken = await encrypt(token);

    const response = await this._api.post(
      `/recaptcha-verify`,
      {
        token: encryptedToken,
        encrypted: true,
        useEnterprise: true,
      },
      {
        cancelToken: config.cancelToken,
      }
    );
    return response.data;
  }

  async verifyLandArable(
    lat: number,
    lng: number,
    cancelToken?: CancelToken
  ): Promise<{ arable_land: boolean }> {
    try {
      const response = await this._api.get('/arableLand', {
        params: { longitude: lng, latitude: lat },
        cancelToken,
      });
      return response.data;
    } catch (e) {
      console.log('Error!!', e);
    }
    return { arable_land: true };
  }
  async checkAvailableFields(
    detectFieldPayload: DetectFieldPayload,
    cancelToken?: CancelToken
  ): Promise<DetectFieldResponse> {
    const startTime = performance.now();
    const body: any = detectFieldPayload;
    const response = await this._api.post('/detectField', body, {
      cancelToken,
    });
    const totalTimeTaken = (performance.now() - startTime) / 1000;
    if (response?.data?.isDigifarm) {
      track('digifarm low resolution response time', { time: `${totalTimeTaken} seconds` });
    } else {
      track('gris response time', { time: `${totalTimeTaken} seconds` });
    }
    return response.data;
  }
  async getHighResolutionFields(
    payload: { fields: string[]; countryCode: string },
    cancelToken?: CancelToken
  ): Promise<DetectFieldResponse> {
    const startTime = performance.now();
    const body: any = payload;
    const response = await this._api.post('/digifarmHighRes', body, {
      cancelToken,
    });
    const totalTimeTaken = (performance.now() - startTime) / 1000;
    track('digifarm high resolution response time', { time: `${totalTimeTaken} seconds` });
    return response.data;
  }
  async getToken(): Promise<any> {
    const response = await this._api.get('/token');
    return response.data;
  }
  async getNonDDGUserDetails(email: string, cancelToken: CancelToken): Promise<UserDetails> {
    const response = await this._api.get('/user-details', {
      params: { login: email },
      cancelToken: cancelToken,
    });
    return response.data;
  }
  async getGrowthStage(
    growthStagePayload: GrowthStagePayload,
    cancelToken?: CancelToken
  ): Promise<any> {
    const body: any = growthStagePayload;
    const response = await this._api.post('/growth-stage', body, {
      cancelToken,
    });
    return response.data;
  }
  async getAbioticStage(
    abioticStagePayload: AbioticStagePayload,
    cancelToken?: CancelToken
  ): Promise<any> {
    const body: AbioticStagePayload = abioticStagePayload;
    const response = await this._api.post('/abiotic-stage', body, {
      cancelToken,
    });
    return response.data;
  }
  async updateGrowersEmailForVaReport(
    vaReportPayload: VaReportPayload,
    cancelToken?: CancelToken
  ): Promise<any> {
    const body: VaReportPayload = vaReportPayload;
    const response = await this._api.patch('/va-report', body, {
      cancelToken,
    });
    return response.data;
  }

  async getVaReportStatus(vaReportId: string, cancelToken?: CancelToken): Promise<any> {
    const response = await this._api.post(
      '/va-report-status',
      { report_id: vaReportId },
      { cancelToken }
    );
    return response.data;
  }

  async shareRecommendation(payload: {
    email: string;
    placeholders: {
      grower_name: string;
      tsr_name: string;
      report_link: string;
      solution_created_on: string;
    };
    sender: string;
    language: string;
  }): Promise<any> {
    try {
      const response = await this._api.post('/share-recommendation', payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error in shareRecommendation:', error);
      throw error;
    }
  }

  async updateRecommendation({ data }: { data: any }): Promise<any> {
    try {
      const response = await this._api.post(`/update-recommendation`, data, {});
      return response.data;
    } catch (error) {
      console.error('Error in updateRecommendation:', error);
      throw error;
    }
  }

  async preBookSeeds(
    payload: PreBookSeedsPayload,
    cancelToken?: CancelToken
  ): Promise<PreBookSeedsResponse> {
    try {
      const response = await this._api.post('/sip-integration', payload, {
        cancelToken,
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error in preBookSeeds:', error);
      throw error;
    }
  }

  async getLayouts(cancelToken?: CancelToken): Promise<any> {
    const currentEnv = process.env.REACT_APP_ENV;
    const isLayoutEnabled = currentEnv === 'uat' || currentEnv === 'prod';
    if (!isLayoutEnabled) {
      return null;
    }

    try {
      const response = await this._api_uat.get('/get-layouts', {
        cancelToken,
      });
      return response.data;
    } catch (error) {
      console.error('Error in getLayouts:', error);
      throw error;
    }
  }

  async generateOsrSummary({ data }: { data: any }): Promise<any> {
    try {
      const response = await this._api.post(`/osr-summary`, data, {});
      return response.data;
    } catch (error) {
      console.error('Error in generateOsrSummary:', error);
      throw error;
    }
  }

  async fetchOsrSummary({ solutionId }: { solutionId: string }): Promise<string> {
    try {
      const response = await this._api.get(`/get-osr-summary?id=${solutionId}`, {});
      return response.data;
    } catch (error) {
      console.error('Error in fetchOsrSummary:', error);
      throw error;
    }
  }
}

export default new ProxyLayer();
